import { useEffect, useRef, useState } from 'react';
import { exchangeAuthorizationCode, accessTokenExpiresAtFromResponse } from '../utils/googleAuth';

/**
 * Handles redirect from Google OAuth (PKCE). Exchanges ?code= for tokens,
 * loads profile, persists user_auth, then returns to /.
 */
export default function OAuthCallback() {
  const didRun = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const err = params.get('error');
      const desc = params.get('error_description');
      if (err) {
        setError(desc || err || 'Sign-in was cancelled or failed.');
        return;
      }
      const code = params.get('code');
      const state = params.get('state');
      if (!code || !state) {
        setError('Missing authorization code. Try signing in again.');
        return;
      }

      try {
        const tokens = await exchangeAuthorizationCode(code, state);
        const userResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokens.access_token}` },
        });
        if (!userResponse.ok) {
          throw new Error('Could not load Google profile.');
        }
        const userData = await userResponse.json();

        const userAuth = {
          access_token: tokens.access_token,
          imageUrl: userData.picture,
          name: userData.name,
          email: userData.email,
          accessTokenExpiresAt: accessTokenExpiresAtFromResponse(tokens),
          expiry: Date.now() + 7 * 24 * 60 * 60 * 1000,
        };
        if (tokens.refresh_token) {
          userAuth.refresh_token = tokens.refresh_token;
        }

        localStorage.setItem('user_auth', JSON.stringify(userAuth));
        window.history.replaceState({}, '', '/');
        window.location.assign(`${window.location.origin}/`);
      } catch (e) {
        console.error(e);
        setError(e?.message || 'Sign-in failed.');
      }
    };

    run();
  }, []);

  if (error) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'var(--bg-dark, #1a1a1a)',
          color: 'var(--text-primary, #fff)',
        }}
      >
        <p style={{ marginBottom: 16, maxWidth: 420, textAlign: 'center' }}>{error}</p>
        <a href="/" style={{ color: 'var(--link-on-dark, #93c5fd)' }}>
          Back to Deck Pack
        </a>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-dark, #1a1a1a)',
        color: 'var(--text-primary, #fff)',
      }}
    >
      Signing you in…
    </div>
  );
}
